import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useLanguage, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import PageHeader from "../components/layout/PageHeader";
import CharacterSelector from "../components/shared/CharacterSelector";
import { toast } from "sonner";
import { Bell, Moon, Globe, LogOut, Info, Heart, Edit2, Loader2, ChevronRight, BarChart3, Clock, Volume2, Plus, Trash2, Play, X, Check, Settings as SettingsIcon, Sparkles, Zap, User, Home, BookOpen, Brain, Target, Bookmark, CheckCircle } from "lucide-react";

const DAY_OPTIONS = [
  { value: "mon", label: "Sen", labelEn: "Mon" },
  { value: "tue", label: "Sel", labelEn: "Tue" },
  { value: "wed", label: "Rab", labelEn: "Wed" },
  { value: "thu", label: "Kam", labelEn: "Thu" },
  { value: "fri", label: "Jum", labelEn: "Fri" },
  { value: "sat", label: "Sab", labelEn: "Sat" },
  { value: "sun", label: "Min", labelEn: "Sun" },
];

const SOUND_OPTIONS = [
  { value: "default", label: "Default", labelId: "Standar" },
  { value: "gentle", label: "Gentle", labelId: "Lembut" },
  { value: "energetic", label: "Energetic", labelId: "Semangat" },
  { value: "spiritual", label: "Spiritual", labelId: "Spiritual" },
  { value: "silent", label: "Silent", labelId: "Hening" },
];

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const { language, setLanguage, translation, setTranslation, themePreset, setThemePreset, accentColor, setAccentColor, quranFontSize, setQuranFontSize, dashboardPrefs, setDashboardPrefs, favoriteShortcuts, setFavoriteShortcuts } = useLanguage();
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const isEnglish = language === "en";
  const [activeTab, setActiveTab] = useState("general");
  const [leaderboardOptOut, setLeaderboardOptOut] = useState(user?.leaderboard_opt_out ?? false);

  // Edit profile states
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    gender: user?.gender || "",
    avatar_character_id: user?.avatar_character_id || null
  });
  const [isSaving, setIsSaving] = useState(false);

  // Reminders state
  const [reminders, setReminders] = useState([]);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [isSavingReminder, setIsSavingReminder] = useState(false);
  const [isLoadingReminders, setIsLoadingReminders] = useState(true);

  // Reminder form state
  const [formData, setFormData] = useState({
    habit_id: null,
    title: "",
    message: "",
    reminder_time: "08:00",
    frequency: "daily",
    days: [],
    sound: "default",
  });

  const fetchReminders = async () => {
    try {
      setIsLoadingReminders(true);
      const response = await apiClient.get("/reminders");
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setIsLoadingReminders(false);
    }
  };

  const fetchReminderSettings = async () => {
    try {
      const response = await apiClient.get("/reminder-settings");
      setReminderSettings(response.data.settings);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  // Load reminders and settings on mount
  useEffect(() => {
    fetchReminders();
    fetchReminderSettings();
  }, []);

  useEffect(() => {
    setLeaderboardOptOut(user?.leaderboard_opt_out ?? false);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    toast.success(isEnglish ? "Logged out successfully" : "Berhasil keluar");
  };

  const toggleDarkMode = (val) => {
    setDarkMode(val);
    document.documentElement.classList.toggle("dark", val);
  };
  
  const updateDashboardPref = (key, value) => {
    const next = { ...(dashboardPrefs || {}), [key]: value };
    setDashboardPrefs(next);
  };
  
  const toggleFavoriteShortcut = (path) => {
    const list = Array.isArray(favoriteShortcuts) ? favoriteShortcuts.slice() : [];
    const idx = list.indexOf(path);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      list.push(path);
    }
    setFavoriteShortcuts(list);
  };

  const handleEditProfile = () => {
    setProfileData({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      gender: user?.gender || "",
      avatar_character_id: user?.avatar_character_id || null
    });
    setShowEditProfile(true);
  };

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);

      // Validate
      if (!profileData.name.trim()) {
        toast.error(isEnglish ? "Name is required" : "Nama harus diisi");
        return;
      }

      if (!profileData.email.trim()) {
        toast.error(isEnglish ? "Email is required" : "Email harus diisi");
        return;
      }

      // Build update payload with only changed fields
      const updateData = {};
      if (profileData.name !== user?.name) updateData.name = profileData.name.trim();
      if (profileData.email !== user?.email) updateData.email = profileData.email.trim();
      if (profileData.phone !== user?.phone) updateData.phone = profileData.phone.trim() || null;
      if (profileData.gender !== user?.gender) updateData.gender = profileData.gender || null;
      if (profileData.avatar_character_id !== user?.avatar_character_id) updateData.avatar_character_id = profileData.avatar_character_id;

      if (Object.keys(updateData).length === 0) {
        setShowEditProfile(false);
        return;
      }

      // Call API
      await apiClient.patch("/auth/me", updateData);

      // Update user state
      const updatedUser = { ...user, ...updateData };
      setUser(updatedUser);

      toast.success(isEnglish ? "Profile updated successfully" : "Profil berhasil diperbarui");
      setShowEditProfile(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMsg = error.response?.data?.detail || (isEnglish ? "Failed to update profile" : "Gagal memperbarui profil");
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLeaderboardToggle = async (value) => {
    try {
      setLeaderboardOptOut(value);
      await apiClient.patch("/auth/me", { leaderboard_opt_out: value });
      setUser({ ...user, leaderboard_opt_out: value });
      toast.success(isEnglish ? "Leaderboard preference updated" : "Preferensi leaderboard diperbarui");
    } catch (error) {
      console.error("Error updating leaderboard preference:", error);
      toast.error(isEnglish ? "Failed to update preference" : "Gagal memperbarui preferensi");
      setLeaderboardOptOut(user?.leaderboard_opt_out ?? false);
    }
  };

  const handleRequestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      toast.error(isEnglish ? "Notifications are not supported" : "Notifikasi tidak didukung");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        toast.success(isEnglish ? "Permission granted" : "Izin diberikan");
      } else if (permission === "denied") {
        toast.error(isEnglish ? "Permission blocked" : "Izin diblokir");
      } else {
        toast.message(isEnglish ? "Permission not set" : "Izin belum diatur");
      }
    } catch (error) {
      toast.error(isEnglish ? "Failed to request permission" : "Gagal meminta izin");
    }
  };

  // Reminder functions
  const handleCreateReminder = () => {
    setEditingReminder(null);
    setFormData({
      habit_id: null,
      title: "",
      message: "",
      reminder_time: "08:00",
      frequency: "daily",
      days: [],
      sound: reminderSettings?.default_sound || "default",
    });
    setShowReminderDialog(true);
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setFormData({
      habit_id: reminder.habit_id,
      title: reminder.title,
      message: reminder.message,
      reminder_time: reminder.reminder_time,
      frequency: reminder.frequency,
      days: reminder.days || [],
      sound: reminder.sound,
    });
    setShowReminderDialog(true);
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!confirm(isEnglish ? "Are you sure you want to delete this reminder?" : "Yakin ingin menghapus pengingat ini?")) {
      return;
    }

    try {
      await apiClient.delete(`/reminders/${reminderId}`);
      toast.success(isEnglish ? "Reminder deleted" : "Pengingat dihapus");
      fetchReminders();
    } catch (error) {
      console.error("Error deleting reminder:", error);
      toast.error(isEnglish ? "Failed to delete reminder" : "Gagal menghapus pengingat");
    }
  };

  const handleToggleReminder = async (reminder) => {
    try {
      await apiClient.put(`/reminders/${reminder.reminder_id}`, {
        enabled: !reminder.enabled,
      });
      toast.success(isEnglish ? "Reminder updated" : "Pengingat diperbarui");
      fetchReminders();
    } catch (error) {
      console.error("Error toggling reminder:", error);
      toast.error(isEnglish ? "Failed to update reminder" : "Gagal memperbarui pengingat");
    }
  };

  const handleTestReminder = async (reminderId) => {
    try {
      const response = await apiClient.post(`/reminders/${reminderId}/test`);
      toast.success(isEnglish ? "Test notification sent" : "Notifikasi tes dikirim");
    } catch (error) {
      console.error("Error testing reminder:", error);
      toast.error(isEnglish ? "Failed to send test notification" : "Gagal mengirim notifikasi tes");
    }
  };

  const handleSaveReminder = async () => {
    try {
      setIsSavingReminder(true);

      // Validate
      if (!formData.title.trim()) {
        toast.error(isEnglish ? "Title is required" : "Judul harus diisi");
        return;
      }

      if (!formData.message.trim()) {
        toast.error(isEnglish ? "Message is required" : "Pesan harus diisi");
        return;
      }

      if (formData.frequency === "specific_days" && formData.days.length === 0) {
        toast.error(isEnglish ? "Select at least one day" : "Pilih minimal satu hari");
        return;
      }

      const payload = {
        ...formData,
        days: formData.frequency === "specific_days" ? formData.days : [],
      };

      if (editingReminder) {
        await apiClient.put(`/reminders/${editingReminder.reminder_id}`, payload);
        toast.success(isEnglish ? "Reminder updated" : "Pengingat diperbarui");
      } else {
        await apiClient.post("/reminders", payload);
        toast.success(isEnglish ? "Reminder created" : "Pengingat dibuat");
      }

      setShowReminderDialog(false);
      fetchReminders();
    } catch (error) {
      console.error("Error saving reminder:", error);
      const errorMsg = error.response?.data?.detail || (isEnglish ? "Failed to save reminder" : "Gagal menyimpan pengingat");
      toast.error(errorMsg);
    } finally {
      setIsSavingReminder(false);
    }
  };

  const handleUpdateReminderSettings = async (field, value) => {
    try {
      if (field === "notification_enabled" && value === true) {
        if (!("Notification" in window)) {
          toast.error(isEnglish ? "Notifications are not supported in this browser" : "Notifikasi tidak didukung di browser ini");
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          toast.error(isEnglish ? "Notification permission denied" : "Izin notifikasi ditolak");
          return;
        }
      }

      const newSettings = { ...reminderSettings, [field]: value };
      setReminderSettings(newSettings);

      await apiClient.put("/reminder-settings", { [field]: value });
      toast.success(isEnglish ? "Settings updated" : "Pengaturan diperbarui");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error(isEnglish ? "Failed to update settings" : "Gagal memperbarui pengaturan");
      setReminderSettings(reminderSettings);
    }
  };

  const toggleDay = (dayValue) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(dayValue)
        ? prev.days.filter((d) => d !== dayValue)
        : [...prev.days, dayValue],
    }));
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getFrequencyText = (reminder) => {
    if (reminder.frequency === "daily") {
      return isEnglish ? "Every day" : "Setiap hari";
    } else if (reminder.frequency === "weekly") {
      return isEnglish ? "Every week" : "Setiap minggu";
    } else if (reminder.frequency === "specific_days") {
      if (!reminder.days || reminder.days.length === 0) return "-";
      const dayNames = reminder.days.map((d) => {
        const day = DAY_OPTIONS.find((opt) => opt.value === d);
        return isEnglish ? day.labelEn : day.label;
      });
      return dayNames.join(", ");
    }
    return "-";
  };

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <PageHeader title={isEnglish ? "Settings" : "Pengaturan"} />
      <div className="page-container space-y-4 sm:space-y-5">
        {/* Profile Card */}
        <Card>
          <CardContent className="p-3 sm:p-4 md:p-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <Avatar className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex-shrink-0">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary text-primary-foreground text-base sm:text-lg md:text-xl">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-sm sm:text-base md:text-lg truncate">{user?.name || "User"}</h2>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">{user?.email}</p>
                {user?.phone && (
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">{user.phone}</p>
                )}
                {user?.gender && (
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {isEnglish ? "Gender" : "Jenis Kelamin"}: {user.gender === "male" ? (isEnglish ? "Male" : "Pria") : (isEnglish ? "Female" : "Wanita")}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/profile")}
                className="shrink-0 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
              >
                <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                <span className="hidden xs:inline">{isEnglish ? "Profile" : "Profil"}</span>
                <span className="xs:hidden">{isEnglish ? "Profile" : "Profil"}</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Premium Custom Tabs */}
        <Card className="overflow-visible sm:overflow-hidden">
          {/* Premium Tab Header */}
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background -m-1 p-1 rounded-t-lg">
            <div className="flex gap-1">
              {[
                { value: "general", label: isEnglish ? "Umum" : "Umum", icon: SettingsIcon, badge: null },
                { value: "reminders", label: isEnglish ? "Pengingat" : "Pengingat", icon: Bell, badge: reminders.filter(r => r.enabled).length }
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.value;

                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`flex-1 relative flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium text-[10px] sm:text-xs md:text-sm transition-all duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-md sm:shadow-lg shadow-primary/25"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-transparent rounded-lg sm:rounded-xl animate-pulse" />
                    )}
                    <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isActive ? "text-white" : ""} flex-shrink-0`} />
                    <span className="relative truncate">{tab.label}</span>
                    {tab.badge !== null && tab.badge > 0 && (
                      <Badge
                        variant={isActive ? "secondary" : "outline"}
                        className={`ml-0.5 sm:ml-1 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] md:text-xs flex-shrink-0 ${
                          isActive
                            ? "bg-white/20 text-white border-white/30"
                            : "bg-primary/10 text-primary border-primary/20"
                        }`}
                      >
                        {tab.badge}
                      </Badge>
                    )}
                    {isActive && (
                      <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-pulse" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <CardContent className="p-3 sm:p-4 md:p-6">
            {/* General Settings Tab */}
            {activeTab === "general" && (
              <div className="space-y-4 sm:space-y-5 md:space-y-6">
            {/* Preferences Header */}
            <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg sm:rounded-xl border border-blue-200 dark:border-blue-900 mb-3 sm:mb-4 md:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md sm:shadow-lg flex-shrink-0">
                <SettingsIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold truncate">{isEnglish ? "Preferences" : "Preferensi"}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Customize your experience" : "Kustomisasi pengalaman Anda"}</p>
              </div>
            </div>

            {/* Preferences */}
            <section>
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 flex items-center justify-center flex-shrink-0">
                        <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm sm:text-base">{isEnglish ? "Notifications" : "Notifikasi"}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Enable dhikr reminders" : "Aktifkan pengingat dzikir"}</p>
                      </div>
                    </div>
                    <Switch checked={notifications} onCheckedChange={setNotifications} className="flex-shrink-0" />
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm sm:text-base">{isEnglish ? "Show on Leaderboard" : "Tampilkan di Leaderboard"}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Allow your profile to appear on leaderboard" : "Izinkan profil tampil di leaderboard"}</p>
                      </div>
                    </div>
                    <Switch checked={!leaderboardOptOut} onCheckedChange={(val) => handleLeaderboardToggle(!val)} className="flex-shrink-0" />
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center flex-shrink-0">
                        <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm sm:text-base">{isEnglish ? "Dark Mode" : "Mode Gelap"}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Comfortable dark theme" : "Tampilan gelap untuk mata"}</p>
                      </div>
                    </div>
                    <Switch checked={darkMode} onCheckedChange={toggleDarkMode} className="flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </section>

            <section>
              <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-2 sm:mb-3 px-1">{isEnglish ? "Personalization" : "Personalisasi"}</h3>
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-sm sm:text-base truncate">{isEnglish ? "Islamic Theme" : "Tema Islami"}</p>
                    </div>
                    <Select value={themePreset} onValueChange={setThemePreset}>
                      <SelectTrigger className="w-36 sm:w-40 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">{isEnglish ? "Default" : "Default"}</SelectItem>
                        <SelectItem value="mecca">{isEnglish ? "Mecca Pattern" : "Motif Makkah"}</SelectItem>
                        <SelectItem value="mosque">{isEnglish ? "Mosque Silhouette" : "Siluet Masjid"}</SelectItem>
                        <SelectItem value="calligraphy">{isEnglish ? "Arabic Calligraphy" : "Kaligrafi Arab"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-sm sm:text-base truncate">{isEnglish ? "Accent Color" : "Warna Aksen"}</p>
                    </div>
                    <Select value={accentColor} onValueChange={setAccentColor}>
                      <SelectTrigger className="w-36 sm:w-40 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="teal">Teal</SelectItem>
                        <SelectItem value="emerald">Emerald</SelectItem>
                        <SelectItem value="indigo">Indigo</SelectItem>
                        <SelectItem value="rose">Rose</SelectItem>
                        <SelectItem value="amber">Amber</SelectItem>
                        <SelectItem value="violet">Violet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-sm sm:text-base truncate">{isEnglish ? "Quran Font Size" : "Ukuran Huruf Quran"}</p>
                    </div>
                    <Select value={quranFontSize} onValueChange={setQuranFontSize}>
                      <SelectTrigger className="w-36 sm:w-40 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">{isEnglish ? "Small" : "Kecil"}</SelectItem>
                        <SelectItem value="normal">{isEnglish ? "Normal" : "Normal"}</SelectItem>
                        <SelectItem value="large">{isEnglish ? "Large" : "Besar"}</SelectItem>
                        <SelectItem value="xlarge">{isEnglish ? "Extra Large" : "Sangat Besar"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section>
              <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-2 sm:mb-3 px-1">{isEnglish ? "Dashboard" : "Dasbor"}</h3>
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <p className="font-medium text-sm sm:text-base">{isEnglish ? "Show Daily Verse" : "Tampilkan Ayat Harian"}</p>
                    <Switch checked={dashboardPrefs?.showDailyVerse ?? true} onCheckedChange={(v) => updateDashboardPref("showDailyVerse", v)} />
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <p className="font-medium text-sm sm:text-base">{isEnglish ? "Show Prayer Times" : "Tampilkan Jadwal Shalat"}</p>
                    <Switch checked={dashboardPrefs?.showPrayerTimes ?? true} onCheckedChange={(v) => updateDashboardPref("showPrayerTimes", v)} />
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <p className="font-medium text-sm sm:text-base">{isEnglish ? "Show Habits" : "Tampilkan Kebiasaan"}</p>
                    <Switch checked={dashboardPrefs?.showHabits ?? true} onCheckedChange={(v) => updateDashboardPref("showHabits", v)} />
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <p className="font-medium text-sm sm:text-base">{isEnglish ? "Show Progress Sharing" : "Tampilkan Bagikan Progress"}</p>
                    <Switch checked={dashboardPrefs?.showProgressSharing ?? true} onCheckedChange={(v) => updateDashboardPref("showProgressSharing", v)} />
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <p className="font-medium text-sm sm:text-base">{isEnglish ? "Show Quote" : "Tampilkan Kutipan"}</p>
                    <Switch checked={dashboardPrefs?.showQuote ?? true} onCheckedChange={(v) => updateDashboardPref("showQuote", v)} />
                  </div>
                </CardContent>
              </Card>
            </section>

            <section>
              <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-2 sm:mb-3 px-1">{isEnglish ? "Quick Access Shortcuts" : "Shortcut Akses Cepat"}</h3>
              <Card>
                <CardContent className="p-3 sm:p-4 md:p-5">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { path: "/home", label: isEnglish ? "Home" : "Beranda", Icon: Home },
                      { path: "/quran", label: "Quran", Icon: BookOpen },
                      { path: "/hafalan", label: isEnglish ? "Memorize" : "Hafalan", Icon: Brain },
                      { path: "/habits", label: isEnglish ? "Habits" : "Kebiasaan", Icon: CheckCircle },
                      { path: "/prayer-times", label: isEnglish ? "Prayer Times" : "Jadwal", Icon: Clock },
                      { path: "/doa", label: isEnglish ? "Duas" : "Doa", Icon: Heart },
                      { path: "/quiz", label: "Quiz", Icon: Target },
                      { path: "/bookmarks", label: isEnglish ? "Bookmarks" : "Bookmark", Icon: Bookmark },
                      { path: "/analytics", label: isEnglish ? "Analytics" : "Analitik", Icon: BarChart3 },
                    ].map(item => {
                      const selected = Array.isArray(favoriteShortcuts) && favoriteShortcuts.includes(item.path);
                      return (
                        <Button
                          key={item.path}
                          variant={selected ? "default" : "outline"}
                          size="sm"
                          className="gap-1"
                          onClick={() => toggleFavoriteShortcut(item.path)}
                          title={item.label}
                        >
                          <item.Icon className="w-4 h-4" />
                          {item.label}
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </section>
            {/* Language */}
            <section>
              <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-2 sm:mb-3 px-1">{isEnglish ? "Language" : "Bahasa"}</h3>
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-sm sm:text-base truncate">{isEnglish ? "App Language" : "Bahasa Aplikasi"}</p>
                    </div>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger className="w-28 sm:w-32 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="id">Indonesia</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-sm sm:text-base truncate">{isEnglish ? "Quran Translation" : "Terjemahan Quran"}</p>
                    </div>
                    <Select value={translation} onValueChange={setTranslation}>
                      <SelectTrigger className="w-28 sm:w-32 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="indonesian">Indonesia</SelectItem>
                        <SelectItem value="english">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Features Links */}
            <section>
              <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-2 sm:mb-3 px-1">{isEnglish ? "Features" : "Fitur"}</h3>
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  <Link to="/analytics" className="block">
                    <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm sm:text-base">{isEnglish ? "Analytics" : "Analitik"}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "View detailed progress analytics" : "Lihat analitik progress detail"}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* About */}
            <section>
              <h3 className="text-xs sm:text-sm font-medium text-muted-foreground mb-2 sm:mb-3 px-1">{isEnglish ? "About" : "Tentang"}</h3>
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                      <Info className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-sm sm:text-base">{isEnglish ? "App Version" : "Versi Aplikasi"}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">1.0.0</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                      <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-sm sm:text-base">Quran Companion</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">{isEnglish ? "Made with love for Ummah" : "Dibuat dengan cinta untuk Ummah"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
              </div>
            )}

            {/* Reminders Tab */}
            {activeTab === "reminders" && (
              <div className="space-y-4 sm:space-y-5 md:space-y-6">
            {/* Reminders Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-lg sm:rounded-xl border border-amber-200 dark:border-amber-900 mb-3 sm:mb-4 md:mb-6 gap-2">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-md sm:shadow-lg flex-shrink-0">
                  <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold truncate">{isEnglish ? "Reminder Settings" : "Pengaturan Pengingat"}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {reminders.filter(r => r.enabled).length} {isEnglish ? "active reminders" : "pengingat aktif"}
                  </p>
                </div>
              </div>
              <Button onClick={handleCreateReminder} size="sm" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 flex-shrink-0">
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                <span className="hidden xs:inline">{isEnglish ? "New Reminder" : "Pengingat Baru"}</span>
                <span className="xs:hidden">{isEnglish ? "New" : "Baru"}</span>
              </Button>
            </div>

            {/* Quick Notification Settings */}
            <Card>
              <CardContent className="p-3 sm:p-4 md:p-5">
                <div className="flex items-center justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400" />
                    <h4 className="font-semibold text-sm sm:text-base">{isEnglish ? "Quick Settings" : "Pengaturan Cepat"}</h4>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleRequestNotificationPermission}>
                    {isEnglish ? "Request Permission" : "Minta izin"}
                  </Button>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  {/* Enable Notifications */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm sm:text-base">{isEnglish ? "Enable Notifications" : "Aktifkan Notifikasi"}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Receive reminder notifications" : "Terima notifikasi pengingat"}</p>
                    </div>
                    <Switch
                      checked={reminderSettings?.notification_enabled ?? true}
                      onCheckedChange={(val) => handleUpdateReminderSettings("notification_enabled", val)}
                      className="flex-shrink-0"
                    />
                  </div>

                  {/* Default Sound */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm sm:text-base">{isEnglish ? "Default Sound" : "Suara Default"}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Notification sound" : "Suara notifikasi"}</p>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                      <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" />
                      <Select
                        value={reminderSettings?.default_sound || "default"}
                        onValueChange={(val) => handleUpdateReminderSettings("default_sound", val)}
                      >
                        <SelectTrigger className="w-28 sm:w-32 sm:w-36 h-8 sm:h-9 text-xs sm:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SOUND_OPTIONS.map((sound) => (
                            <SelectItem key={sound.value} value={sound.value} className="text-xs sm:text-sm">
                              {isEnglish ? sound.label : sound.labelId}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Quiet Hours */}
                  <div className="space-y-2 sm:space-y-3 pt-2 sm:pt-3 border-t">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm sm:text-base">{isEnglish ? "Quiet Hours" : "Jam Tenang"}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Disable notifications during specific hours" : "Nonaktifkan notifikasi pada jam tertentu"}</p>
                      </div>
                      <Switch
                        checked={reminderSettings?.quiet_hours_enabled ?? false}
                        onCheckedChange={(val) => handleUpdateReminderSettings("quiet_hours_enabled", val)}
                        className="flex-shrink-0"
                      />
                    </div>

                    {reminderSettings?.quiet_hours_enabled && (
                      <div className="flex items-center gap-2 sm:gap-3 pl-3 sm:pl-4">
                        <Moon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
                        <Input
                          type="time"
                          value={reminderSettings?.quiet_hours_start || "22:00"}
                          onChange={(e) => handleUpdateReminderSettings("quiet_hours_start", e.target.value)}
                          className="w-24 sm:w-28 sm:w-32 h-8 sm:h-9 text-xs sm:text-sm"
                        />
                        <span className="text-muted-foreground text-sm">-</span>
                        <Input
                          type="time"
                          value={reminderSettings?.quiet_hours_end || "06:00"}
                          onChange={(e) => handleUpdateReminderSettings("quiet_hours_end", e.target.value)}
                          className="w-24 sm:w-28 sm:w-32 h-8 sm:h-9 text-xs sm:text-sm"
                        />
                      </div>
                    )}
                  </div>

                  {/* Snooze Duration */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div>
                      <p className="font-medium">{isEnglish ? "Snooze Duration" : "Durasi Tidur"}</p>
                      <p className="text-sm text-muted-foreground">{isEnglish ? "Default snooze time" : "Waktu tidur default"}</p>
                    </div>
                    <Select
                      value={String(reminderSettings?.snooze_duration || 5)}
                      onValueChange={(val) => handleUpdateReminderSettings("snooze_duration", parseInt(val))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 min</SelectItem>
                        <SelectItem value="10">10 min</SelectItem>
                        <SelectItem value="15">15 min</SelectItem>
                        <SelectItem value="30">30 min</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reminders List */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{isEnglish ? "My Reminders" : "Pengingat Saya"}</h2>
              <Button onClick={handleCreateReminder} size="sm">
                <Plus className="w-4 h-4 mr-1" />
                {isEnglish ? "Add Reminder" : "Tambah Pengingat"}
              </Button>
            </div>

            {isLoadingReminders ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : reminders.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">{isEnglish ? "No reminders yet" : "Belum ada pengingat"}</p>
                  <p className="text-sm text-muted-foreground mb-4">{isEnglish ? "Create your first reminder to stay on track" : "Buat pengingat pertama Anda untuk tetap on track"}</p>
                  <Button onClick={handleCreateReminder}>
                    <Plus className="w-4 h-4 mr-2" />
                    {isEnglish ? "Create Reminder" : "Buat Pengingat"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {reminders.map((reminder) => (
                  <Card key={reminder.reminder_id} className={!reminder.enabled ? "opacity-60" : ""}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Clock className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold truncate">{reminder.title}</h4>
                              <p className="text-sm text-muted-foreground truncate">{reminder.message}</p>
                            </div>
                            <Switch
                              checked={reminder.enabled}
                              onCheckedChange={() => handleToggleReminder(reminder)}
                              className="shrink-0"
                            />
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <Badge variant="secondary" className="font-medium">
                              {formatTime(reminder.reminder_time)}
                            </Badge>
                            <span className="text-muted-foreground text-xs">{getFrequencyText(reminder)}</span>
                            {reminder.habit_name && (
                              <Badge variant="outline" className="text-xs">
                                {reminder.habit_name}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {isEnglish ? SOUND_OPTIONS.find(s => s.value === reminder.sound)?.label : SOUND_OPTIONS.find(s => s.value === reminder.sound)?.labelId}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleTestReminder(reminder.reminder_id)}
                            className="h-8 w-8"
                            title={isEnglish ? "Test notification" : "Tes notifikasi"}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditReminder(reminder)}
                            className="h-8 w-8"
                            title={isEnglish ? "Edit" : "Ubah"}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteReminder(reminder.reminder_id)}
                            className="h-8 w-8 text-destructive"
                            title={isEnglish ? "Delete" : "Hapus"}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logout Button */}
        <Button variant="destructive" className="w-full" onClick={handleLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> {isEnglish ? "Logout" : "Keluar"}
        </Button>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{isEnglish ? "Edit Profile" : "Ubah Profil"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4 py-3 sm:py-4">
            {/* Name */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="name" className="text-sm">{isEnglish ? "Name" : "Nama"} *</Label>
              <Input
                id="name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                placeholder={isEnglish ? "Enter your name" : "Masukkan nama Anda"}
                className="text-sm"
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="email" className="text-sm">{isEnglish ? "Email" : "Email"} *</Label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                placeholder={isEnglish ? "Enter your email" : "Masukkan email Anda"}
                className="text-sm"
              />
            </div>

            {/* Phone */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="phone" className="text-sm">{isEnglish ? "Phone" : "Telepon"}</Label>
              <Input
                id="phone"
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                placeholder={isEnglish ? "Enter your phone number" : "Masukkan nomor telepon"}
                className="text-sm"
              />
            </div>

            {/* Gender */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="gender" className="text-sm">{isEnglish ? "Gender" : "Jenis Kelamin"}</Label>
              <Select
                value={profileData.gender}
                onValueChange={(value) => setProfileData({ ...profileData, gender: value })}
              >
                <SelectTrigger id="gender" className="text-sm">
                  <SelectValue placeholder={isEnglish ? "Select gender" : "Pilih jenis kelamin"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male" className="text-sm">{isEnglish ? "Male" : "Pria"}</SelectItem>
                  <SelectItem value="female" className="text-sm">{isEnglish ? "Female" : "Wanita"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Character Avatar */}
            <div className="space-y-1.5 sm:space-y-2 pt-2 border-t">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-primary" />
                <Label className="text-sm font-medium">{isEnglish ? "Avatar Karakter" : "Avatar Karakter"}</Label>
              </div>
              <div className="flex justify-end mb-2">
                <Link to="/profile/edit" className="text-xs sm:text-sm text-primary hover:underline">
                  {isEnglish ? "Edit profile in full page" : "Ubah profil di halaman penuh"}
                </Link>
              </div>
              <CharacterSelector
                selectedCharacter={profileData.avatar_character_id}
                onSelect={(characterId) => setProfileData({ ...profileData, avatar_character_id: characterId })}
                size="sm"
                gender={profileData.gender}
              />
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                {isEnglish ? "Pilih karakter untuk representasi profilmu" : "Pilih karakter untuk representasi profilmu"}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowEditProfile(false)}
              disabled={isSaving}
              className="text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-4"
            >
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            <Button onClick={handleSaveProfile} disabled={isSaving} className="text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-4">
              {isSaving ? (
                <>
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                  {isEnglish ? "Saving..." : "Menyimpan..."}
                </>
              ) : (
                isEnglish ? "Save" : "Simpan"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              {editingReminder ? (isEnglish ? "Edit Reminder" : "Ubah Pengingat") : (isEnglish ? "Create Reminder" : "Buat Pengingat")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4 py-3 sm:py-4">
            {/* Title */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="r-title" className="text-sm">{isEnglish ? "Title" : "Judul"} *</Label>
              <Input
                id="r-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={isEnglish ? "e.g., Morning Prayer" : "Contoh: Sholat Pagi"}
                className="text-sm"
              />
            </div>

            {/* Message */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="r-message" className="text-sm">{isEnglish ? "Message" : "Pesan"} *</Label>
              <Input
                id="r-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={isEnglish ? "e.g., Don't forget to pray Fajr" : "Contoh: Jangan lupa sholat Subuh"}
                className="text-sm"
              />
            </div>

            {/* Time */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="r-time" className="text-sm">{isEnglish ? "Reminder Time" : "Waktu Pengingat"} *</Label>
              <Input
                id="r-time"
                type="time"
                value={formData.reminder_time}
                onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value })}
                className="text-sm"
              />
            </div>

            {/* Frequency */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="r-frequency" className="text-sm">{isEnglish ? "Frequency" : "Frekuensi"}</Label>
              <Select
                value={formData.frequency}
                onValueChange={(val) => setFormData({ ...formData, frequency: val, days: [] })}
              >
                <SelectTrigger id="r-frequency" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily" className="text-sm">{isEnglish ? "Every day" : "Setiap hari"}</SelectItem>
                  <SelectItem value="weekly" className="text-sm">{isEnglish ? "Every week" : "Setiap minggu"}</SelectItem>
                  <SelectItem value="specific_days" className="text-sm">{isEnglish ? "Specific days" : "Hari tertentu"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Days Selection */}
            {formData.frequency === "specific_days" && (
              <div className="space-y-1.5 sm:space-y-2">
                <Label className="text-sm">{isEnglish ? "Select Days" : "Pilih Hari"}</Label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {DAY_OPTIONS.map((day) => {
                    const isSelected = formData.days.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-xs sm:text-sm font-medium transition-colors ${
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted-foreground/10"
                        }`}
                      >
                        {isEnglish ? day.labelEn : day.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sound */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="r-sound" className="text-sm">{isEnglish ? "Notification Sound" : "Suara Notifikasi"}</Label>
              <Select
                value={formData.sound}
                onValueChange={(val) => setFormData({ ...formData, sound: val })}
              >
                <SelectTrigger id="r-sound" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOUND_OPTIONS.map((sound) => (
                    <SelectItem key={sound.value} value={sound.value} className="text-sm">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        {isEnglish ? sound.label : sound.labelId}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowReminderDialog(false)}
              disabled={isSavingReminder}
              className="text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-4"
            >
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            <Button onClick={handleSaveReminder} disabled={isSavingReminder} className="text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-4">
              {isSavingReminder ? (
                <>
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                  {isEnglish ? "Saving..." : "Menyimpan..."}
                </>
              ) : (
                <>
                  <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  {isEnglish ? "Save" : "Simpan"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
